/**
 * Пороги, множители и шкалы балльной оценки GO / COND / NO-GO (ТЗ 11.11, WS 15587410 п.3).
 *
 * Перенос 1:1 из Launch Pipeline (`frontend/src/shared/lib/calculations/constants.ts` —
 * блок балльной оценки PLP-23 и таблицы нишевых критериев из утверждённого проекта
 * `docs/mf/PLP-23-niche-criteria-draft.md`). Одни и те же числа в обоих софтах: иначе
 * калькулятор и карточка лаунча давали бы по одному товару разный вердикт. Меняются
 * только вместе с пайплайном.
 */

export type Recommendation = 'GO' | 'CONDITIONAL' | 'NO_GO';
export type ScoringBlock = 'unitEconomics' | 'demand' | 'competition' | 'logistics';
export type AutoRejectReason = 'PROFIT_NEGATIVE' | 'ROI_BELOW_MIN' | 'MARGIN_BELOW_MIN';
export type FragilityLevel = 'low' | 'medium' | 'high';
export type DifferentiationLevel = 'none' | 'low' | 'medium' | 'high';
export type ScoreGroup = 'A' | 'B' | 'C';

// Автоотказы — приоритет над баллами.
export const AUTO_REJECT_MIN_PROFIT_USD = 0.0;
export const AUTO_REJECT_MIN_ROI_PERCENT = 20.0;
export const AUTO_REJECT_MIN_MARGIN_PERCENT = 15.0;

// Балл критерия = нормированное значение 0..1 × множитель × базовые 100.
export const BASE_CRITERION_POINTS = 100.0;
export const MULTIPLIER_PROFIT = 2.0;
export const MULTIPLIER_ROI = 2.0;
export const MULTIPLIER_MARGIN = 1.5;
export const MULTIPLIER_WEIGHT = 1.0;
export const MULTIPLIER_FRAGILITY = 1.0;
export const MULTIPLIER_DIFFERENTIATION = 1.0;
export const NICHE_CRITERION_MULTIPLIER = 1.0;

// Группы по сумме баллов (полная оценка, ниша привязана).
export const POINTS_GROUP_THRESHOLD_A = 2000.0;
export const POINTS_GROUP_THRESHOLD_B = 1200.0;
// Предварительный вердикт по доле набранных баллов (ниша не привязана).
export const SCORE_RATIO_GO = 0.8;
export const SCORE_RATIO_CONDITIONAL = 0.6;

export const UNIT_ECONOMICS_CRITERIA_COUNT = 6;
export const DEMAND_COMPETITION_CRITERIA_COUNT = 14;
export const LOGISTICS_CRITERIA_COUNT = 3;
export const TOTAL_SCORING_CRITERIA =
  UNIT_ECONOMICS_CRITERIA_COUNT + DEMAND_COMPETITION_CRITERIA_COUNT + LOGISTICS_CRITERIA_COUNT;

// Шкалы юнит-экономики и логистики.
export const PROFIT_SCORE_T4_USD = 12.0;
export const PROFIT_SCORE_T3_USD = 6.0;
export const PROFIT_SCORE_T2_USD = 3.0;
export const PROFIT_SCORE_T1_USD = 1.0;
export const ROI_SCORE_T4_PCT = 200.0;
export const ROI_SCORE_T3_PCT = 100.0;
export const ROI_SCORE_T2_PCT = 50.0;
export const ROI_SCORE_T1_PCT = 20.0;
export const MARGIN_SCORE_T2_PCT = 25.0;
export const MARGIN_SCORE_T1_PCT = 15.0;
export const WEIGHT_SCORE_T4_KG = 0.3;
export const WEIGHT_SCORE_T3_KG = 0.5;
export const WEIGHT_SCORE_T2_KG = 1.0;
export const WEIGHT_SCORE_T1_KG = 2.0;
export const FRAGILITY_SCORE: Record<FragilityLevel, number> = { low: 1.0, medium: 0.5, high: 0.0 };
export const DIFFERENTIATION_SCORE: Record<DifferentiationLevel, number> = {
  high: 1.0,
  medium: 0.6,
  low: 0.3,
  none: 0.0,
};

// Нишевые критерии: шкала 0/1/2 (тернарные) или 0/1 (бинарные). Доли: 0.15 = 15%.
export const NICHE_MAX_POINT_TERNARY = 2;
export const NICHE_MAX_POINT_BINARY = 1;
export const NICHE_DEMAND_VOLUME_HIGH = 50_000.0; // SV90 ≥ → 2
export const NICHE_DEMAND_VOLUME_MID = 20_000.0; // SV90 ≥ → 1
export const NICHE_REAL_DEMAND_HIGH = 5_000.0; // SV90 × CR ≥ → 2
export const NICHE_REAL_DEMAND_MID = 2_000.0; // ≥ → 1
export const NICHE_CONVERSION_GOOD_LOW = 0.03; // 3–10% → 2
export const NICHE_CONVERSION_GOOD_HIGH = 0.1;
export const NICHE_CONVERSION_OVERHEATED = 0.15; // >15% → 0 (перегрета/спам)
export const NICHE_DEMAND_GROWTH90_HIGH = 0.1; // >+10% → 2
export const NICHE_DEMAND_GROWTH90_FLAT = 0.0; // 0…+10% → 1; <0 → 0
export const NICHE_YOY_TREND_MIN = 0.0; // YoY >0 → 1
export const NICHE_ACTUAL_SALES_HIGH = 2_400.0; // ≥2400/год → 2
export const NICHE_ACTUAL_SALES_MID = 1_200.0; // ≥1200 → 1
export const NICHE_PRICE_MIN = 15.0; // $15–70 → 1
export const NICHE_PRICE_MAX = 70.0;
export const NICHE_SATURATION_LOW = 200.0; // товаров <200 → 2
export const NICHE_SATURATION_MID = 500.0; // ≤500 → 1
export const NICHE_TOP5_CONCENTRATION_LOW = 0.5; // доля кликов топ-5 <50% → 2
export const NICHE_TOP5_CONCENTRATION_MID = 0.7; // ≤70% → 1
export const NICHE_REVIEWS_LOW = 500.0; // отзывы лидера <500 → 2
export const NICHE_REVIEWS_MID = 1_000.0; // ≤1000 → 1
export const NICHE_RATING_WEAK = 4.4; // рейтинг лидера <4.4 → 2
export const NICHE_RATING_MID = 4.6; // ≤4.6 → 1
export const NICHE_RETURN_LOW = 0.05; // возвраты <5% → 2
export const NICHE_RETURN_MID = 0.1; // ≤10% → 1
